import { useState } from 'react';
import { getFirestore, doc, updateDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import Swal from 'sweetalert2';
import firebaseApp from '../../../firebase/credenciales';
import { logBusinessAction } from '../../../services/AuditService';
import { generarAsientosContables } from '../../../services/planilla/generarAsientosContables';
import { actualizarEstadoPlanillaEnAsientosContables, sincronizarAsientosPlanillaPorPlanilla } from '../../../services/contabilidad/asientosService';

const db = getFirestore(firebaseApp);

/**
 * Hook personalizado para manejar operaciones de planillas
 * @param {Array} planillas - Lista de planillas
 * @param {Function} recargarPlanillas - Función para recargar planillas
 * @returns {Object} - Funciones para operaciones de planillas
 */
export const usePlanillasOperations = (planillas, recargarPlanillas) => {
  const [operacionLoading, setOperacionLoading] = useState(false);

  // Buscar planilla por ID
  const encontrarPlanilla = (planillaId) => {
    return planillas.find(p => p.id === planillaId);
  };

  // Procesar planilla
  const procesarPlanilla = async (planillaId) => {
    try {
      // NO activar loading aún, solo después de confirmar
      // setOperacionLoading(true); // <--- Eliminado de aquí

      if (!planillaId || typeof planillaId !== 'string') {
        throw new Error("ID de planilla inválido");
      }

      const planilla = encontrarPlanilla(planillaId);
      if (!planilla) {
        throw new Error("Planilla no encontrada");
      }

      if (planilla.estado !== "BORRADOR") {
        Swal.fire({
          icon: "warning",
          title: "Estado Inválido",
          text: `Solo se pueden procesar planillas en estado BORRADOR. Estado actual: ${planilla.estado}`,
        });
        return;
      }

      if (!planilla.total_empleados || planilla.total_empleados <= 0) {
        Swal.fire({
          icon: "warning",
          title: "Planilla Vacía",
          text: "No se puede procesar una planilla sin empleados",
        });
        return;
      }

      const result = await Swal.fire({
        title: "¿Procesar planilla?",
        text: "Se generarán los asientos contables para la provisión y el pago",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#8B4513",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sí, procesar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        setOperacionLoading(true); // Activar solo durante el proceso real
        console.log("🔄 Procesando planilla localmente:", planillaId);

        try {
          // 1. Generar asientos contables localmente
          console.log("🧮 Generando asientos contables localmente...");
          await generarAsientosContables(planillaId);
          console.log("✅ Asientos generados");

          // 1.1 Sincronizar automáticamente con módulo contable
          await sincronizarAsientosPlanillaPorPlanilla({
            rucEmpresa: planilla.ruc_empresa,
            planillaId,
            estadoPlanilla: "PROCESADO",
          });
          console.log("✅ Asientos sincronizados a cont_asientos");

          // 2. Actualizar estado de la planilla a PROCESADO
          await updateDoc(doc(db, "pl_planillas", planillaId), {
            estado: "PROCESADO",
            fecha_procesamiento: new Date().toISOString()
          });

          // AUDITORÍA
          try {
            await logBusinessAction("PROCESAMIENTO_PLANILLA", "GestionPlanillas", {
              planillaId,
              cambiosRealizados: {
                estado: { anterior: "BORRADOR", nuevo: "PROCESADO" }
              }
            });
          } catch (e) {
            console.warn("Error en auditoría:", e);
          }

          // Desactivar loading ANTES del alert para no bloquearlo
          setOperacionLoading(false);

          await Swal.fire({
            icon: "success",
            title: "Planilla procesada",
            text: "Se generaron los asientos y la planilla pasó a estado PROCESADO.",
          });

          recargarPlanillas();

        } catch (localError) {
          console.error("❌ Error en procesamiento local:", localError);
          Swal.fire({
            icon: "error",
            title: "Error de Procesamiento",
            text: "No se pudieron generar los asientos o actualizar el estado: " + localError.message
          });
        }
      }
    } catch (error) {
      console.error("❌ Error al procesar planilla (General):", error);
      Swal.fire({
        icon: "error",
        title: "Error Inesperado",
        text: error.message
      });
    } finally {
      setOperacionLoading(false);
    }
  };

  // Aprobar planilla
  const aprobarPlanilla = async (planillaId) => {
    try {
      // setOperacionLoading(true); // Eliminado de aquí

      if (!planillaId || typeof planillaId !== 'string') {
        throw new Error("ID de planilla inválido");
      }

      const planilla = encontrarPlanilla(planillaId);
      if (!planilla) {
        throw new Error("Planilla no encontrada");
      }

      if (planilla.estado !== "PROCESADO") {
        Swal.fire({
          icon: "warning",
          title: "Estado Inválido",
          text: `Solo se pueden aprobar planillas en estado PROCESADO. Estado actual: ${planilla.estado}`,
        });
        return;
      }

      const result = await Swal.fire({
        title: "¿Aprobar planilla?",
        text: "Una vez aprobada, no se podrá modificar ni eliminar",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#8B4513",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sí, aprobar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        setOperacionLoading(true);
        console.log("✅ Aprobando planilla localmente:", planillaId);

        try {
          // Actualización directa de estado a APROBADO
          await updateDoc(doc(db, "pl_planillas", planillaId), {
            estado: "APROBADO",
            fecha_aprobacion: new Date().toISOString()
          });

          // AUDITORÍA
          try {
            await logBusinessAction("CIERRE_PLANILLA", "GestionPlanillas", {
              planillaId,
              cambiosRealizados: {
                estado: { anterior: "PROCESADO", nuevo: "APROBADO" }
              }
            });
          } catch (e) {
            console.warn("Error en auditoría:", e);
          }

          // Sincronizar estado en contabilidad de forma automática
          await actualizarEstadoPlanillaEnAsientosContables({
            rucEmpresa: planilla.ruc_empresa,
            planillaId,
            estadoPlanilla: "APROBADO",
          });

          setOperacionLoading(false);

          await Swal.fire({
            icon: "success",
            title: "Planilla aprobada",
            text: "La planilla ha sido aprobada exitosamente.",
          });

          recargarPlanillas();

        } catch (localError) {
          console.error("❌ Error en aprobación local:", localError);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "No se pudo cambiar el estado a APROBADO: " + localError.message
          });
        }
      }
    } catch (error) {
      console.error("❌ Error al aprobar planilla:", error);
      Swal.fire({
        icon: "error",
        title: "Error Inesperado",
        text: error.message
      });
    } finally {
      setOperacionLoading(false);
    }
  };

  // Eliminar planilla (Operación Local)
  const eliminarPlanilla = async (planillaId) => {
    try {
      const planilla = encontrarPlanilla(planillaId);
      if (!planilla) {
        throw new Error("Planilla no encontrada");
      }

      // No permitir eliminar si está aprobada
      if (planilla.estado === "APROBADO") {
        await Swal.fire({
          icon: "error",
          title: "No permitido",
          text: "No se puede eliminar una planilla ya aprobada.",
        });
        return;
      }

      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Se eliminarán la planilla, sus detalles y los asientos contables asociados permanentemente.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        setOperacionLoading(true);
        console.log("🗑️ Eliminando planilla localmente:", planillaId);

        const batch = writeBatch(db);

        // 1. Eliminar detalles de la planilla (pl_planilla_detalle)
        const detallesQuery = query(
          collection(db, "pl_planilla_detalle"),
          where("planilla_id", "==", planillaId)
        );
        const detallesSnapshot = await getDocs(detallesQuery);
        detallesSnapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });

        // 2. Eliminar asientos contables asociados (pl_asientos_contables)
        const asientosQuery = query(
          collection(db, "pl_asientos_contables"),
          where("planilla_id", "==", planillaId)
        );
        const asientosSnapshot = await getDocs(asientosQuery);
        asientosSnapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });

        // 3. Revertir estado de adelantos asociados (si existen)
        const adelantosIds = [];
        detallesSnapshot.forEach((docSnap) => {
          const detalle = docSnap.data();
          if (detalle.adelantos && Array.isArray(detalle.adelantos)) {
            detalle.adelantos.forEach(a => adelantosIds.push(a.id));
          }
        });

        if (adelantosIds.length > 0) {
          console.log(`🔄 Revirtiendo ${adelantosIds.length} adelantos a 'POR DESCONTAR'`);
          for (const id of adelantosIds) {
            batch.update(doc(db, "pl_adelantos", id), {
              estado: "POR DESCONTAR",
              updated_at: new Date()
            });
          }
        }

        // 4. Eliminar documento principal de la planilla (pl_planillas)
        const planillaRef = doc(db, "pl_planillas", planillaId);
        batch.delete(planillaRef);

        // 5. Eliminar asientos contables sincronizados (cont_asientos)
        const asientosContablesQuery = query(
          collection(db, "cont_asientos"),
          where("origen_id", "==", planillaId)
        );
        const asientosContablesSnapshot = await getDocs(asientosContablesQuery);
        asientosContablesSnapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });

        // Ejecutar todas las eliminaciones y actualizaciones en una sola transacción
        await batch.commit();

        // AUDITORÍA
        try {
          await logBusinessAction("ELIMINACION_PLANILLA", "GestionPlanillas", {
            planillaId,
            estado: planilla.estado
          });
        } catch (e) {
          console.warn("Error en auditoría:", e);
        }

        // IMPORTANTE: Set loading false ANTES de mostrar el alert de éxito
        // para evitar que el spinner bloquee el botón de aceptar del modal
        setOperacionLoading(false);

        await Swal.fire(
          "Eliminado",
          "La planilla y todos sus datos relacionados han sido eliminados correctamente.",
          "success"
        );

        recargarPlanillas();
      }
    } catch (error) {
      setOperacionLoading(false);
      console.error("❌ Error al eliminar planilla:", error);
      Swal.fire({
        icon: "error",
        title: "Error de Eliminación",
        text: "No se pudo completar la eliminación local: " + error.message,
      });
    } finally {
      // Ya lo manejamos arriba para evitar carreras, pero por seguridad:
      setOperacionLoading(false);
    }
  };

  return {
    operacionLoading,
    procesarPlanilla,
    aprobarPlanilla,
    eliminarPlanilla
  };
};
